/* eslint-disable import/named, import/no-named-as-default, import/no-named-as-default-member */
import { useSelector } from 'react-redux';
import type { MessageTranslator } from '@oneflowab/pomes';
import { take } from 'lodash';
import { formatDateString } from '@flow/libs/date';
import positionsReducer from '@app/reducers/entities/positions';
import { useUserEvents } from '@app/contexts/user-events';

import Button from '@app/components/button';
import ParticipantBubble, { SimpleBubble } from '@app/components/contract-card/participant-bubble';

import BellIcon from '@app/components/icons/bell';
import { EditMenuItem } from '@app/components/menu-items/edit';
import ActionsMenu from '@app/components/actions-menu';
import { PositionTooltip } from '@app/components/position-tooltip';
import getInternalReminders from '@app/components/contract-internal-reminders-list/get-internal-reminders';

import isPastEvent from '@flow/libs/utils/is-past-event';

import style from './mini-internal-reminder-card.module.scss';

type EventType = ReturnType<typeof getInternalReminders>[number]['sourceEvent'];

type MiniInternalReminderCardProps = {
  agreement: Oneflow.Agreement;
  contractInternalReminderEvent: EventType;
  message: MessageTranslator;
};

const MiniInternalReminderCard = ({
  contractInternalReminderEvent: reminderEvent,
  message,
  agreement,
}: MiniInternalReminderCardProps) => {
  const MAX_VISIBLE_RECIPIENTS = 5;
  const NUMBER_OF_RECIPIENTS_WHEN_LIMITED = MAX_VISIBLE_RECIPIENTS - 1;

  let event = reminderEvent;
  // delayed events for a specific agreement does not include the agreement object in response
  if (!event?.agreement && agreement) {
    event = {
      ...reminderEvent,
      agreement,
    };
  }

  const allRecipients = useSelector((state) =>
    positionsReducer.getPositionsSelector(state, {
      ids: event?.customReminderData.recipients,
    }),
  );

  const hasPassed = isPastEvent(event.date);

  const { dateFormat, openModal, setSelectedEvent } = useUserEvents();
  const formattedDate = formatDateString(event.date, dateFormat);

  const reminderTense = hasPassed
    ? message({
        id: 'Sent',
        comment: 'Label for notifying a contract reminder event was sent on a date',
      })
    : message({
        id: 'Will be sent on',
        comment: 'Label for notifying a contract reminder event will be sent for an upcoming date',
      });

  const insightTextWithDate = `${reminderTense} ${formattedDate}`;

  const recipientBubbles =
    allRecipients.length > MAX_VISIBLE_RECIPIENTS
      ? take(allRecipients, NUMBER_OF_RECIPIENTS_WHEN_LIMITED)
      : take(allRecipients, MAX_VISIBLE_RECIPIENTS);

  const getEditMenuItem = () => (
    <EditMenuItem
      onClick={() => {
        setSelectedEvent(event);
        openModal();
      }}
      disabled={!event.isEditable}
    />
  );

  const renderActionMenu = () => {
    const actions = [
      getEditMenuItem(),
    ];

    // Return null if there are no actions
    if (actions.length === 0) {
      return null;
    }

    return (
      <ActionsMenu
        actions={actions}
        focusOnCloseDisabled
      />
    );
  };

  return (
    <div className={style.MiniInternalReminderCard}>
      <div className={style.MiniInternalReminderCardInformation}>
        <div className={style.MiniInternalReminderCardSubjectRow}>
          <Button
            kind="linkSeparate"
            onClick={() => {
              setSelectedEvent(event);
              openModal();
            }}
          >
            <span className={style.SubjectText}>{event.customReminderData.subject}</span>
          </Button>
        </div>
        <div className={style.MiniInternalReminderCardInsight}>
          <BellIcon height="12px" />
          {insightTextWithDate}
        </div>
      </div>
      <div className={style.MiniInternalReminderCardRecipients}>
        {recipientBubbles.map((position) => (
          <PositionTooltip
            key={position.id}
            position={position}
          >
            <span>
              <ParticipantBubble
                participant={position}
                className={style.MiniInternalReminderCardRecipientBubble}
                isContractMarkedAsSigned={false}
                userBadgeDiameter={32}
              />
            </span>
          </PositionTooltip>
        ))}
        {allRecipients.length > recipientBubbles.length && (
          <div className={style.MiniInternalReminderCardBubbleTooltip}>
            <SimpleBubble
              label={`+${allRecipients.length - recipientBubbles.length}`}
              className={style.MiniInternalReminderCardRecipientBubble}
              diameter={32}
            />
          </div>
        )}
      </div>
      {renderActionMenu()}
    </div>
  );
};

export default MiniInternalReminderCard;
